export interface ReportExportFilenameInput {
  siteName?: string | null;
  reportType?: string | null;
  view?: 'screening-summary' | 'report-summary' | null;
  version?: number | string | null;
  minorVersion?: number | null;
  format?: 'pdf' | 'docx' | 'pptx' | 'csv';
}

const SUMMARY_LABEL_BY_REPORT_TYPE: Readonly<Record<string, string>> = {
  feasibility: 'Screening Summary',
  environmental: 'Environmental Summary',
  'power-availability': 'Generation & Impact Summary',
  'test-fit': 'Test Fit Summary',
  interconnection: 'Connectivity Summary',
  'supply-chain-risk': 'Supply Chain Summary',
  financial: 'Financial Summary',
  'public-sentiment': 'Public Sentiment Summary',
  'custom-brief': 'Custom Brief Summary',
  'executive-summary': 'Executive Summary',
  'water-resource': 'Water Resource Summary',
};

const FULL_REPORT_LABEL_BY_REPORT_TYPE: Readonly<Record<string, string>> = {
  feasibility: 'Screening Report',
  environmental: 'Environmental Report',
  'power-availability': 'Generation & Impact Report',
  'test-fit': 'Test Fit Report',
  interconnection: 'Connectivity Report',
  'supply-chain-risk': 'Supply Chain Report',
  financial: 'Financial Report',
  'public-sentiment': 'Public Sentiment Report',
  'custom-brief': 'Custom Brief Report',
  'executive-summary': 'Executive Summary Report',
  'water-resource': 'Water Resource Report',
};

export function sanitizeExportSiteName(siteName?: string | null): string {
  const cleaned = String(siteName ?? '')
    .replace(/[<>:"/\\|?*\x00-\x1f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || 'Site';
}

export function formatHumanReportVersion(
  version?: number | string | null,
  minorVersion?: number | null,
): string | null {
  if (typeof version === 'string' && version.trim()) {
    const trimmed = version.trim().replace(/^v/i, '');
    if (/^\d+\.\d+$/.test(trimmed)) return `v${trimmed}`;
    if (/^\d+$/.test(trimmed)) {
      const minor = typeof minorVersion === 'number' && Number.isFinite(minorVersion) && minorVersion >= 0
        ? Math.floor(minorVersion)
        : 0;
      return `v${trimmed}.${minor}`;
    }
    return null;
  }
  if (typeof version === 'number' && Number.isFinite(version) && version >= 1) {
    const major = Math.floor(version);
    const minor = typeof minorVersion === 'number' && Number.isFinite(minorVersion) && minorVersion >= 0
      ? Math.floor(minorVersion)
      : 0;
    return `v${major}.${minor}`;
  }
  return null;
}

export function resolveReportExportKindLabel(
  reportType?: string | null,
  view?: 'screening-summary' | 'report-summary' | null,
): string {
  const normalizedType = String(reportType ?? '').trim() || 'feasibility';
  if (view === 'screening-summary') {
    return 'Screening Summary';
  }
  if (view === 'report-summary') {
    return SUMMARY_LABEL_BY_REPORT_TYPE[normalizedType] ?? 'Report Summary';
  }
  return FULL_REPORT_LABEL_BY_REPORT_TYPE[normalizedType] ?? 'Screening Report';
}

export function buildReportExportFilename(input: ReportExportFilenameInput): string {
  const site = sanitizeExportSiteName(input.siteName);
  const kind = resolveReportExportKindLabel(input.reportType, input.view);
  const version = formatHumanReportVersion(input.version, input.minorVersion);
  const ext = input.format ?? 'pdf';
  return version
    ? `${site} - ${kind} - ${version}.${ext}`
    : `${site} - ${kind}.${ext}`;
}
